import { EXTERMINATION_OUTCOMES, EXTERMINATION_OUTCOME_LABELS, PEST_TYPES } from '@sitewise/shared';
import { Button, Label } from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import imageCompression from 'browser-image-compression';
import { Camera, ChevronLeft, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  fetchVendorUnitLogs,
  submitUnitLogSignoff,
  vendorPresignUpload,
} from '../../api/vendor-work-orders.js';
import { SignaturePad } from '../../components/signature-pad.js';

async function uploadSignature(blob: Blob): Promise<string> {
  const presign = await vendorPresignUpload({
    kind: 'work_order_signature',
    contentType: 'image/png',
    filename: 'signature.png',
    sizeBytes: blob.size,
  });
  await fetch(presign.uploadUrl, { method: 'PUT', headers: presign.headers, body: blob });
  return presign.uploadId;
}

async function uploadCompressedPhoto(file: File): Promise<string> {
  // Force JPEG: iOS cameras produce HEIC/HEIF, which isn't an allowed
  // `work_order_photo` content type and would 400 at presign.
  const compressed = await imageCompression(file, {
    maxWidthOrHeight: 1920,
    initialQuality: 0.85,
    fileType: 'image/jpeg',
  });
  const jpegName = `${file.name.replace(/\.[^.]+$/, '') || 'photo'}.jpg`;
  const presign = await vendorPresignUpload({
    kind: 'work_order_photo',
    contentType: 'image/jpeg',
    filename: jpegName,
    sizeBytes: compressed.size,
  });
  await fetch(presign.uploadUrl, { method: 'PUT', headers: presign.headers, body: compressed });
  return presign.uploadId;
}

interface FormState {
  outcome: (typeof EXTERMINATION_OUTCOMES)[number];
  notes: string;
  pestTypes: string[];
  photoUploadIds: string[];
  signatureUploadId: string | null;
}

const EMPTY_FORM: FormState = {
  outcome: 'serviced',
  notes: '',
  pestTypes: [],
  photoUploadIds: [],
  signatureUploadId: null,
};

export function VendorUnitLogsPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['vendor', 'unit-logs', id],
    queryFn: () => fetchVendorUnitLogs(id!),
    enabled: Boolean(id),
  });

  const [activeLogId, setActiveLogId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [photoPreviews, setPhotoPreviews] = useState<Array<{ url: string; uploadId: string }>>([]);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resetSheet = () => {
    setActiveLogId(null);
    setForm(EMPTY_FORM);
    setPhotoPreviews([]);
    setSignaturePreview(null);
    setError(null);
  };

  const submit = useMutation({
    mutationFn: async () => {
      if (!id || !activeLogId) throw new Error('Missing context');
      if (!form.signatureUploadId) throw new Error('Signature is required');
      await submitUnitLogSignoff(id, activeLogId, {
        outcome: form.outcome,
        treatmentNotes: form.notes.trim() || null,
        pestTypes: form.pestTypes,
        photoUploadIds: form.photoUploadIds,
        signatureUploadId: form.signatureUploadId,
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['vendor', 'unit-logs', id] });
      await qc.invalidateQueries({ queryKey: ['vendor', 'work-order', id] });
      resetSheet();
    },
    onError: (e: Error) => setError(e.message),
  });

  const handleSignature = async (blob: Blob) => {
    setError(null);
    try {
      const uploadId = await uploadSignature(blob);
      setForm((f) => ({ ...f, signatureUploadId: uploadId }));
      setSignaturePreview(URL.createObjectURL(blob));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Signature upload failed');
    }
  };

  const handlePhotos = async (files: FileList | null) => {
    if (!files) return;
    setError(null);
    for (const file of Array.from(files)) {
      try {
        const uploadId = await uploadCompressedPhoto(file);
        const url = URL.createObjectURL(file);
        setPhotoPreviews((p) => [...p, { url, uploadId }]);
        setForm((f) => ({ ...f, photoUploadIds: [...f.photoUploadIds, uploadId] }));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Photo upload failed');
      }
    }
  };

  const togglePest = (p: string) =>
    setForm((f) =>
      f.pestTypes.includes(p)
        ? { ...f, pestTypes: f.pestTypes.filter((x) => x !== p) }
        : { ...f, pestTypes: [...f.pestTypes, p] },
    );

  const progress = data?.progress;
  const pct =
    progress && progress.total > 0
      ? Math.round((progress.signedOff / progress.total) * 100)
      : 0;

  return (
    <div className="space-y-4 pb-8">
      <Link
        to={`/vendor/work/${id}`}
        className="inline-flex items-center gap-1 text-sm text-muted hover:text-fg"
      >
        <ChevronLeft className="h-4 w-4" /> Back to work order
      </Link>

      {data?.building ? (
        <h1 className="text-lg font-semibold text-fg">{data.building.name}</h1>
      ) : null}

      {progress ? (
        <div>
          <p className="text-sm text-muted">
            {progress.signedOff} of {progress.total} units signed off
          </p>
          <div className="mt-2 h-2 w-full rounded-full bg-surface-muted">
            <div className="h-2 rounded-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
        </div>
      ) : null}

      <ul className="space-y-2">
        {data?.items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className="flex min-h-12 w-full items-center justify-between rounded-lg border border-border bg-surface px-4 py-3 text-left active:bg-surface-muted disabled:opacity-60"
              onClick={() => {
                if (item.status !== 'pending') return;
                setActiveLogId(item.id);
              }}
              disabled={item.status !== 'pending'}
            >
              <span className="font-medium text-fg">Unit {item.unitNumber}</span>
              <span className="text-xs text-muted">
                {item.status === 'signed_off'
                  ? 'Signed off'
                  : item.status === 'no_access'
                    ? 'No access'
                    : 'Pending'}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {activeLogId ? (
        <div role="dialog" aria-modal className="fixed inset-0 z-50 flex items-end bg-black/40">
          <button
            type="button"
            aria-label="Close"
            className="absolute inset-0 h-full w-full cursor-default bg-transparent"
            onClick={resetSheet}
          />
          <div className="relative max-h-[90vh] w-full overflow-y-auto rounded-t-lg bg-surface p-4 sm:mx-auto sm:max-w-lg sm:rounded-lg">
            <h2 className="mb-3 text-base font-semibold">Sign off unit</h2>

            <fieldset className="mb-4">
              <legend className="mb-1 text-sm font-medium text-fg">Outcome</legend>
              <div className="flex flex-wrap gap-2">
                {EXTERMINATION_OUTCOMES.map((o) => (
                  <button
                    key={o}
                    type="button"
                    aria-pressed={form.outcome === o}
                    className={`min-h-11 rounded-md border px-3 py-2 text-sm ${form.outcome === o ? 'border-primary bg-primary-subtle text-primary' : 'border-border'}`}
                    onClick={() => setForm((f) => ({ ...f, outcome: o }))}
                  >
                    {EXTERMINATION_OUTCOME_LABELS[o]}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="mb-4">
              <legend className="mb-1 text-sm font-medium text-fg">Pest types</legend>
              <div className="flex flex-wrap gap-2">
                {PEST_TYPES.map((p) => (
                  <button
                    key={p}
                    type="button"
                    aria-pressed={form.pestTypes.includes(p)}
                    className={`min-h-11 rounded-full border px-3 py-1 text-xs ${form.pestTypes.includes(p) ? 'border-primary bg-primary-subtle text-primary' : 'border-border'}`}
                    onClick={() => togglePest(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </fieldset>

            <Label htmlFor="treatment">Treatment notes</Label>
            <textarea
              id="treatment"
              className="mb-4 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />

            <Label>Photo (optional)</Label>
            <div className="mb-4 flex flex-wrap gap-2">
              {photoPreviews.map((p) => (
                <div
                  key={p.uploadId}
                  className="relative h-16 w-16 overflow-hidden rounded-md border border-border"
                >
                  <img src={p.url} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoPreviews((prev) => prev.filter((x) => x.uploadId !== p.uploadId));
                      setForm((f) => ({
                        ...f,
                        photoUploadIds: f.photoUploadIds.filter((id) => id !== p.uploadId),
                      }));
                    }}
                    className="absolute right-0 top-0 rounded-bl-md bg-black/60 p-0.5 text-white"
                    aria-label="Remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label className="flex h-16 w-16 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border text-muted">
                <Camera className="h-4 w-4" />
                <span className="text-[10px]">Add</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  className="sr-only"
                  onChange={(e) => {
                    void handlePhotos(e.target.files);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>

            <Label>Signature (required)</Label>
            <div className="mb-4">
              {signaturePreview ? (
                <div className="space-y-2">
                  <img
                    src={signaturePreview}
                    alt="Captured signature"
                    className="rounded-md border border-border bg-surface p-2"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSignaturePreview(null);
                      setForm((f) => ({ ...f, signatureUploadId: null }));
                    }}
                  >
                    Sign again
                  </Button>
                </div>
              ) : (
                <SignaturePad
                  onAccept={(blob) => void handleSignature(blob)}
                  uploaded={Boolean(form.signatureUploadId)}
                />
              )}
            </div>

            {error ? (
              <p role="alert" className="mb-2 text-sm text-danger">
                {error}
              </p>
            ) : null}

            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                className="min-h-11 flex-1"
                onClick={resetSheet}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="min-h-11 flex-1"
                onClick={() => submit.mutate()}
                disabled={!form.signatureUploadId || submit.isPending}
              >
                {submit.isPending ? 'Submitting…' : 'Submit'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
